import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Send } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700",
  submitted: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

export default function Templates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", category: "MARKETING", language: "en", header: "", body: "", footer: "", variables: "" });

  const load = async () => setItems((await api.get("/templates")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/templates", { ...f, variables: f.variables.split(",").map(s=>s.trim()).filter(Boolean) });
      toast.success("Template created"); setOpen(false);
      setF({ name:"",category:"MARKETING",language:"en",header:"",body:"",footer:"",variables:"" }); load();
    } catch { toast.error("Failed"); }
  };
  const submit = async (id) => { await api.post(`/templates/${id}/submit`); toast.success("Submitted for approval"); load(); };
  const del = async (id) => { await api.delete(`/templates/${id}`); load(); };

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Message Templates</h1>
          <p className="text-sm text-slate-500">Meta approval required before sending live campaigns</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-template-button"><Plus className="w-4 h-4 mr-1"/>New Template</Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle>New template</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} data-testid="tpl-name-input"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <Select value={f.category} onValueChange={(v)=>setF({...f,category:v})}>
                    <SelectTrigger data-testid="tpl-category-select"><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="MARKETING">Marketing</SelectItem>
                      <SelectItem value="UTILITY">Utility</SelectItem>
                      <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Language</Label><Input value={f.language} onChange={(e)=>setF({...f,language:e.target.value})}/></div>
              </div>
              <div><Label>Header (optional)</Label><Input value={f.header} onChange={(e)=>setF({...f,header:e.target.value})}/></div>
              <div><Label>Body — use {"{{name}}"} for variables</Label>
                <Textarea rows={4} value={f.body} onChange={(e)=>setF({...f,body:e.target.value})} data-testid="tpl-body-input"/></div>
              <div><Label>Footer</Label><Input value={f.footer} onChange={(e)=>setF({...f,footer:e.target.value})}/></div>
              <div><Label>Variables (comma)</Label><Input value={f.variables} onChange={(e)=>setF({...f,variables:e.target.value})} placeholder="name"/></div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-template-button">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((t) => (
          <Card key={t.id} className="p-5 border-slate-200" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.category} · {t.language}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge className={STATUS_STYLE[t.status]}>{t.status}</Badge>
                <Badge variant="outline" className={t.source === "meta" ? "text-emerald-700 border-emerald-200 text-[10px]" : "text-slate-600 border-slate-200 text-[10px]"} data-testid={`tpl-source-${t.id}`}>
                  {t.source === "meta" ? "Meta template" : "Mock template"}
                </Badge>
              </div>
            </div>
            {t.header && <div className="mt-3 text-sm font-medium text-slate-700">{t.header}</div>}
            <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg border border-slate-200">{t.body}</div>
            {t.footer && <div className="text-xs text-slate-500 mt-2">{t.footer}</div>}
            <div className="mt-3 flex gap-2">
              {t.status === "draft" && <Button size="sm" variant="outline" onClick={()=>submit(t.id)} data-testid={`submit-tpl-${t.id}`}><Send className="w-3 h-3 mr-1"/>Submit for approval</Button>}
              <Button size="sm" variant="ghost" onClick={()=>del(t.id)} data-testid={`delete-tpl-${t.id}`}><Trash2 className="w-3 h-3 text-rose-500"/></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
